// Company stores — shared types and plain-words labels for the Flow staff
// pages (S2, CEO 2026-10-12; spec docs/company-stores-mvp.md). The shapes
// mirror the S1 staff API: apps/api/src/routes/company-stores.ts and
// services/company-stores.ts (staffStore / getStore / listStores).
import { ApiError } from "@/lib/api";

export type StoreStatus = "draft" | "awaiting_signoff" | "live" | "paused";
export type PaymentOption = "company_card" | "invoice" | "employee_card";
export type ApprovalRule = "none" | "all" | "over_amount";
export type InvoiceTerms = "net_15" | "net_30" | "net_45" | "net_60" | "net_90";
export type DecorationMethod = "silk-screen" | "embroidery" | "supplier-imprint";
export type StoreDecoration = { method: DecorationMethod; locations: string[] } | null;

/** The fields every staff store payload carries (staffStore). */
export interface StoreBase {
  id: string;
  clientId: string;
  clientName: string | null;
  name: string;
  slug: string;
  status: StoreStatus;
  logoFileId: string | null;
  logoApprovedAt: string | null;
  paymentOptions: PaymentOption[];
  invoiceTerms: InvoiceTerms | null;
  approvalRule: ApprovalRule;
  approvalThreshold: number | null;
  joinDomain: string | null;
  /** Shipping charge by order size (Stephanie 2026-10-22); null = worked out
   *  from each person's address. */
  shippingRanges?: Array<{ from: number; charge: number }> | null;
  /** Last day the store takes orders (YYYY-MM-DD); null = no end date. */
  endsOn?: string | null;
  /** True once the end date has passed (no new orders). */
  ended?: boolean;
  /** The store's delivery addresses; null = each person types their own. */
  shipToList?: StoreAddress[] | null;
  /** Who's billed for every order when it ships to the store's address. */
  billingContactId?: string | null;
  signoffSentAt: string | null;
  signoffContactId: string | null;
  signedOffAt: string | null;
  signoffNote: string | null;
  signoffUrl: string | null;
  repUserId: string | null;
  repName: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /company-stores row. */
export interface StoreListRow extends StoreBase {
  productCount: number;
  memberCount: number;
  orderCount: number;
  pendingApprovalCount: number;
}

/** One product in the editor (getStore). `name` null = left the website. */
export interface StoreProduct {
  id: string;
  productId: string;
  decoration: StoreDecoration;
  colors: string[];
  sort: number;
  active: boolean;
  name: string | null;
  photo: string | null;
  sku: string | null;
  headline: { each: string; at: number; method: string } | null;
  /** Approved virtuals of the store's SAVED logo for this client, in the
   *  store's colours — what employees see instead of the plain photo. */
  mockups?: Array<{ colour: string | null; url: string }>;
}

/** GET /company-stores/:id → store. */
export interface StoreDetail extends StoreBase {
  /** False until Jake's database step for shipping ranges has run. */
  shippingRangesReady?: boolean;
  /** False until Jake's database step for the end date has run. */
  endDateReady?: boolean;
  shipToListReady?: boolean;
  memberCount: number;
  logo: { url: string | null; mimeType: string; name: string } | null;
  products: StoreProduct[];
}

export interface StarterCollection {
  slug: string;
  name: string;
  blurb: string;
  products: Array<{ productId: string; name: string; photo: string | null; decoration: StoreDecoration; priced: boolean }>;
}

export interface StoreMember {
  id: string;
  userId: string;
  role: "admin" | "member";
  name: string;
  email: string;
  invitedAt: string | null;
  joinedAt: string | null;
  state: "active" | "invited";
}

export type StoreOrderStatus = "pending_approval" | "rejected" | "awaiting_payment" | "approved" | "paid" | "placed" | "cancelled";

/** GET /company-stores/:id/orders row (staffOrderView, services/company-store-orders.ts). */
export interface StaffStoreOrder {
  id: string;
  reference: string;
  status: StoreOrderStatus;
  paymentMethod: PaymentOption | null;
  member: { userId: string; name: string | null; email: string | null };
  itemCount: number;
  subtotal: number | null;
  shipping: number | null;
  tax: number | null;
  total: number | null;
  rejectedReason: string | null;
  salesOrderId: string | null;
  orderNumber: number | null;
  createdAt: string;
  approvedAt: string | null;
  paidAt: string | null;
  placedAt: string | null;
  /** Paid, but the payment didn't fit the order: held until the rep checks Stripe. */
  paymentProblem: string | null;
  /** The last reason placement failed (cleared once placed). */
  placementError: string | null;
}

/** How an order was paid, in words. */
export const ORDER_PAID_BY: Record<PaymentOption, string> = {
  company_card: "Company card",
  invoice: "Invoice",
  employee_card: "Employee's card",
};

/** A store order's status in plain words, for the rep. */
export function storeOrderStatus(o: Pick<StaffStoreOrder, "status" | "paymentMethod" | "paymentProblem" | "placementError" | "orderNumber">): {
  text: string;
  tone: "active" | "prospect" | "warn" | "closed";
} {
  if (o.status !== "placed" && o.paymentProblem) return { text: "Paid — check the payment", tone: "warn" };
  if (o.status !== "placed" && o.placementError) return { text: "Couldn't become a sales order", tone: "warn" };
  switch (o.status) {
    case "pending_approval":
      return { text: "Waiting for the client's approval", tone: "prospect" };
    case "rejected":
      return { text: "Declined by the client", tone: "closed" };
    case "awaiting_payment":
      return { text: o.paymentMethod === "company_card" ? "Waiting for the store admin to pay" : "Waiting for payment", tone: "prospect" };
    case "approved":
      return { text: "Approved — being placed", tone: "prospect" };
    case "paid":
      return { text: "Paid — being placed", tone: "prospect" };
    case "placed":
      return { text: o.orderNumber ? `Placed — sales order #${o.orderNumber}` : "Placed", tone: "active" };
    case "cancelled":
      return { text: "Cancelled", tone: "closed" };
    default:
      return { text: String(o.status).replace(/_/g, " "), tone: "prospect" };
  }
}

/** POST /storefront/logos → logo (saveWebsiteLogo). */
export interface UploadedLogo {
  id: string;
  name: string;
  colours: number | null;
  fullColour: boolean;
  palette: string[];
  plan: "screen" | "full-color" | "review";
  note?: string | null;
}

export const STATUS_LABEL: Record<StoreStatus, string> = {
  draft: "Draft",
  awaiting_signoff: "Waiting for sign-off",
  live: "Live",
  paused: "Paused",
};

export const STATUS_TONE: Record<StoreStatus, "prospect" | "warn" | "active" | "closed"> = {
  draft: "prospect",
  awaiting_signoff: "warn",
  live: "active",
  paused: "closed",
};

export const PAYMENT_LABEL: Record<PaymentOption, { label: string; note: string }> = {
  company_card: { label: "Company card", note: "The store admin pays by card for each order." },
  invoice: { label: "Invoice", note: "We invoice the company on its terms." },
  employee_card: { label: "Employee pays by card", note: "Each person pays for their own order." },
};

export const TERMS_LABEL: Record<InvoiceTerms, string> = {
  net_15: "Net 15",
  net_30: "Net 30",
  net_45: "Net 45",
  net_60: "Net 60",
  net_90: "Net 90",
};

/** The client portal, where store people sign in and join. */
export const PORTAL_HOST = "client.withother.com";

/** Same rule as the API's slugify() (services/company-stores.ts). */
export function slugify(name: string): string {
  const s = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50)
    .replace(/-+$/g, "");
  return s || "store";
}

export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** "left-chest" → "left chest". */
function place(loc: string): string {
  return loc.replace(/[-_]+/g, " ").trim();
}

/** How a product is branded, in words. `undefined` = not saved yet (the
 *  server picks the website's usual branding on save). */
export function brandingSummary(d: StoreDecoration | undefined): string {
  if (d === undefined) return "Usual website branding (set when you save)";
  if (d === null) return "No branding method set";
  if (d.method === "supplier-imprint") return "Printed by the maker";
  const how = d.method === "embroidery" ? "Embroidered" : "Screen printed";
  return d.locations.length ? `${how} · ${d.locations.map(place).join(", ")}` : how;
}

export const money = (v: string | number | null | undefined): string =>
  v === null || v === undefined || v === "" || !Number.isFinite(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const shortDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

/** The API answers 503 until Jake runs scripts/push-company-stores.ts. */
export function isNotSetUp(e: unknown): boolean {
  return e instanceof ApiError && e.status === 503;
}

export const NOT_SET_UP_COPY = "Company stores aren't switched on yet (database step pending).";

/** A store delivery address (Stephanie 2026-10-22). */
export interface StoreAddress {
  label?: string | null;
  company?: string | null;
  street1: string;
  street2?: string | null;
  city: string;
  stateCode: string;
  postalCode: string;
  residential: boolean;
}

